export interface Success<D> {
  _tag: 'success'
  data: D
}

export interface Failure<E> {
  _tag: 'failure'
  error: E
}

export type Response<E, D> = Success<D> | Failure<E>

export const failure = <E>(error: E): Failure<E> => ({
  _tag: 'failure',
  error
})

export const success = <D>(data: D): Success<D> => ({
  _tag: 'success',
  data
})

export const successful = (): Success<void> => ({
  _tag: 'success',
  data: undefined
})

export interface StructuredValidationErrors {
  formErrors: string[]
  fieldErrors: Record<string, string[]>
}

export interface ValidationError extends StructuredValidationErrors {
  _tag: 'validation'
}

export const validationError = (
  errors: StructuredValidationErrors
): ValidationError => ({
  _tag: 'validation',
  ...errors
})

export const validationFailure = (
  errors: StructuredValidationErrors
): Failure<ValidationError> => failure(validationError(errors))

export interface Validator<D> {
  (input: unknown): Success<D> | Failure<ValidationError>
}

export const serverError = (
  statusCode: number,
  error: string
): ServerError => ({
  _tag: 'server',
  statusCode,
  message: error
})

export const serverFailure = (
  statusCode: number,
  error: string
): Failure<ServerError> => failure(serverError(statusCode, error))

export interface ServerError {
  _tag: 'server'
  statusCode: number
  message: string
}

export type UnwrappedResponse<D> = Response<ValidationError | ServerError, D>

export type ApiResponse<D> = Promise<Response<ValidationError | ServerError, D>>

export interface ResponseHandlers<D> {
  success: (data: D) => void
  validationError: (error: ValidationError) => void
  serverError: (error: ServerError) => void
}

export const handleApiResponse = <D>(handlers: ResponseHandlers<D>) => (
  response: UnwrappedResponse<D>
): void => {
  if (response._tag === 'success') {
    handlers.success(response.data)
  } else if (response.error._tag == 'validation') {
    handlers.validationError(response.error)
  } else if (response.error._tag == 'server') {
    handlers.serverError(response.error)
  }
}
